import { useParams } from 'react-router-dom';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { IPermissions } from 'common/permissions.types';
import { permissionsUtil } from 'utils/permissionsUtil';
import { getPublisherIdByPortalId } from 'utils/portalUtils';

import { IntegrationSettingsData } from 'components/Settings/Settings.types';

import { ELocalStorageKeys, EQueryKeys } from '../constants/enums';
import useAxios from '../hooks/useAxios';
import { localStorageUtil } from '../utils/localStorageUtil';

import {
  EApiRoutes,
  fetchConfig,
  getApiRouteOrMock
} from './api.utils';
import { useUserDetails } from './useUserDetails';

export default function useIntegration(currentPublisherId?: string) {
  const axios = useAxios();
  const queryClient = useQueryClient();
  const { portalId } = useParams();
  const { userDetails } = useUserDetails();

  const effectivePublisherId = currentPublisherId || (portalId ? getPublisherIdByPortalId(portalId, userDetails) : undefined);
  const publisherHeader = effectivePublisherId ? { 'x-publishers': effectivePublisherId } : undefined;

  const canAccessIntegration = currentPublisherId
    ? !!permissionsUtil.canAccessIntegration(
        localStorageUtil.get<IPermissions>(ELocalStorageKeys.PERMISSIONS) || {},
        currentPublisherId
      )
    : !!portalId;

  const hasPublisherHeader = !!effectivePublisherId;

  const getIntegration = useQuery<IntegrationSettingsData, Error>({
    ...fetchConfig.general,
    queryKey: [EQueryKeys.INTEGRATION, effectivePublisherId],
    queryFn: async () => {
      return await axios.get(
        getApiRouteOrMock(EApiRoutes.INTEGRATION),
        {},
        publisherHeader
      );
    },
    enabled: canAccessIntegration && hasPublisherHeader
  });

  const getPublisherEventsV1 = useQuery<IntegrationSettingsData, Error>({
    ...fetchConfig.general,
    queryKey: ['publisher-events', 'v1', effectivePublisherId],
    queryFn: async () => {
      return await axios.get(
        `${getApiRouteOrMock(EApiRoutes.INTEGRATION)}/publisher-webhook-events-name?version=v1`,
        {},
        publisherHeader
      );
    },
    enabled: canAccessIntegration && hasPublisherHeader
  });

  const getPublisherEventsV2 = useQuery<IntegrationSettingsData, Error>({
    ...fetchConfig.general,
    queryKey: ['publisher-events', 'v2', effectivePublisherId],
    queryFn: async () => {
      return await axios.get(
        `${getApiRouteOrMock(EApiRoutes.INTEGRATION)}/publisher-webhook-events-name?version=v2`,
        {},
        publisherHeader
      );
    },
    enabled: canAccessIntegration && hasPublisherHeader
  });

  const updateIntegration = useMutation(
    async (data: Partial<IntegrationSettingsData>) => {
      return await axios.put(
        getApiRouteOrMock(EApiRoutes.INTEGRATION),
        data,
        publisherHeader
      );
    },
    {
      onSuccess: (updatedIntegrationData) => {
        queryClient.setQueryData(
          [EQueryKeys.INTEGRATION, effectivePublisherId],
          (oldIntegrationData: any) => ({
            ...oldIntegrationData,
            ...updatedIntegrationData
          })
        );
      }
    }
  );

  return {
    getIntegration,
    updateIntegration,
    getPublisherEventsV1,
    getPublisherEventsV2
  };
}
